import { useCallback, useEffect, useRef, useState } from "react";

import { selectSwedishVoice, spokenScheduleText } from "../utils/swedishVoice";

export function createVoiceConversation({ recognitionFactory, synthesis, createUtterance, onText, onSubmit, onState, onError, getPreferredVoice = () => "", delay = 800 }) {
  let active = false, paused = false, hearing = false, recognition = null, utterance = null;
  let hasInterim = false;
  let lastActivity = Date.now();
  let pauseAcknowledged = false, acknowledgement = false;
  let timer, restartTimer, text = "", reply = "", speaking = false;
  const state = () => onState({ active, paused, speaking, listening: active && Boolean(recognition) });
  const silence = () => {
    const hadSpeech = Boolean(utterance);
    if (utterance && !acknowledgement) reply = utterance.text;
    utterance = null;
    speaking = false;
    if (hadSpeech) synthesis.cancel();
  };
  const stop = () => {
    active = false;
    paused = false;
    hearing = false;
    hasInterim = false;
    clearTimeout(timer);
    clearTimeout(restartTimer);
    const previous = recognition;
    recognition = null;
    previous?.abort();
    silence();
    text = "";
    reply = "";
    state();
  };
  const sayNow = (value, isAcknowledgement = false) => {
    const next = createUtterance(spokenScheduleText(value));
    next.rate = 0.96;
    next.pitch = 1;
    next.lang = "sv-SE";
    const voice = selectSwedishVoice(synthesis.getVoices(), getPreferredVoice());
    if (voice) next.voice = voice;
    acknowledgement = isAcknowledgement;
    utterance = next;
    speaking = true;
    state();
    next.onend = () => {
      if (utterance !== next) return;
      utterance = null; speaking = false; acknowledgement = false; state();
    };
    next.onerror = () => {
      if (utterance !== next) return;
      stop(); onError("Rösten kunde inte spelas upp. Starta mikrofonen igen.");
    };
    synthesis.speak(next);
  };
  const currentCommit = () => { recognition?.clearText(); };
  const arm = () => {
    clearTimeout(timer);
    if (!active || paused) return;
    timer = setTimeout(() => {
      if (!active || paused || hearing || hasInterim) return;
      if (text.trim()) {
        const submitted = text.trim();
        text = "";
        reply = "";
        currentCommit();
        onSubmit(submitted);
      } else if (reply) {
        const answer = reply;
        reply = "";
        sayNow(answer);
      }
    }, Math.max(0, delay - (Date.now() - lastActivity)));
  };
  const listen = () => {
    if (!active || recognition) return;
    const current = recognitionFactory();
    recognition = current;
    current.lang = "sv-SE";
    current.continuous = true;
    current.interimResults = true;
    let baseText = text, consumed = 0, resultCount = 0, snapshot = "";
    const ignored = new Set();
    current.onspeechstart = () => {
      if (recognition !== current) return;
      lastActivity = Date.now();
      hearing = true;
      clearTimeout(timer);
      silence();
      state();
    };
    current.onspeechend = () => { if (recognition === current) { hearing = false; arm(); } };
    current.onresult = (event) => {
      if (recognition !== current) return;
      // Resultaten är hela sessionens lista. Bygg om texten i stället för
      // att lägga till slutresultat igen när webbläsaren skickar en uppdatering.
      const results = Array.from(event.results);
      resultCount = results.length;
      const nextSnapshot = JSON.stringify(results.slice(consumed).map((item) => [item[0]?.transcript || "", item.isFinal]));
      if (nextSnapshot === snapshot) return;
      snapshot = nextSnapshot;
      lastActivity = Date.now();
      clearTimeout(timer);
      silence();
      const changedStart = Math.max(consumed, event.resultIndex);
      const latest = results.slice(changedStart).map((item) => item[0]?.transcript || "").join(" ");
      const sessionText = () => results.slice(consumed).filter((item, index) => item.isFinal && !ignored.has(index + consumed)).map((item) => item[0]?.transcript || "").join(" ");
      const ignoreCommand = () => { for (let index = changedStart; index < results.length; index += 1) { if (results[index].isFinal) ignored.add(index); } };
      const command = latest.toLocaleLowerCase("sv").replace(/[.!?,]/g, "").trim();
      if (/^(stopp|sluta)$/.test(command)) {
        paused = true;
        reply = "";
        pauseAcknowledged = false;
        ignoreCommand();
        text = `${baseText} ${sessionText()}`.trim();
        hasInterim = false;
        hearing = false;
        onText(text);
        state();
        return;
      }
      if (/^avsluta(?: samtalet)?$/.test(command)) { stop(); return; }
      if (/^(?:okej |ok )?(?:vänta(?: lite| nu| ett ögonblick)?|avvakta)(?: tack)?$/.test(command)) {
        paused = true;
        // Behåll det användaren redan sagt, men skicka inte pauskommandot.
        ignoreCommand();
        text = `${baseText} ${sessionText()}`.trim();
        onText(text);
        hearing = false;
        hasInterim = false;
        const isFinal = Array.from(event.results).slice(event.resultIndex).every((item) => item.isFinal);
        if (isFinal && !pauseAcknowledged) {
          pauseAcknowledged = true;
          sayNow("Okej, jag väntar.", true);
        }
        state(); return;
      }
      if (command) { paused = false; pauseAcknowledged = false; }
      if (/^(fortsätt|fortsätt nu|du kan svara|svara nu)$/.test(command)) {
        ignoreCommand();
      }
      const interim = results.slice(consumed).filter((item, index) => !item.isFinal && !ignored.has(index + consumed)).map((item) => item[0]?.transcript || "").join(" ").trim();
      text = `${baseText} ${sessionText()}`.trim();
      hasInterim = Boolean(interim);
      onText(`${text} ${interim}`.trim());
      state();
      // En ofärdig igenkänning får inte skickas mitt i ett pågående ord.
      hearing = Boolean(interim);
      if (!interim) arm();
    };
    current.onend = () => {
      if (recognition !== current) return;
      recognition = null;
      hearing = false;
      if (hasInterim) {
        stop();
        onError("Mikrofonen avbröts innan hela meningen kunde bekräftas. Texten finns kvar i skrivfältet så att du kan kontrollera och skicka den.");
        return;
      }
      if (text || reply) arm();
      if (active) restartTimer = setTimeout(listen, 0);
    };
    current.onerror = (event) => {
      if (recognition !== current || event.error === "no-speech") return;
      stop();
      onError(event.error === "not-allowed" ? "Tillåt mikrofonåtkomst för att fortsätta samtalet." : "Mikrofonen avbröts. Starta den igen för att fortsätta.");
    };
    try { current.start(); state(); } catch { stop(); onError("Mikrofonen kunde inte startas."); }
    // Nollställ den aktuella igenkänningens text efter varje skickat meddelande.
    current.clearText = () => { baseText = ""; consumed = resultCount; snapshot = "[]"; hasInterim = false; ignored.clear(); };
  };
  return {
    start() {
      active = true;
      lastActivity = Date.now();
      listen();
    },
    stop,
    reply(value) { if (!active || !value) return; reply = value; arm(); },
    submitted() { text = ""; recognition?.clearText(); },
  };
}

export default function useVoiceConversation({ isOpen, scopeKey, onText, onSubmit, onError }) {
  const callbacks = useRef({ onText, onSubmit, onError });
  useEffect(() => { callbacks.current = { onText, onSubmit, onError }; });
  useEffect(() => { window.speechSynthesis?.getVoices(); }, []);
  const controller = useRef(null);
  const [state, setState] = useState({ active: false, paused: false, speaking: false, listening: false });
  const stop = useCallback(() => controller.current?.stop(), []);
  const speak = useCallback((text) => controller.current?.reply(text), []);
  const toggle = useCallback(() => {
    if (controller.current) { controller.current.stop(); controller.current = null; return; }
    const Recognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!Recognition || !window.speechSynthesis || !window.SpeechSynthesisUtterance) {
      callbacks.current.onError("Röstsamtal stöds inte i den här webbläsaren."); return;
    }
    controller.current = createVoiceConversation({
      recognitionFactory: () => new Recognition(), synthesis: window.speechSynthesis,
      createUtterance: (text) => new window.SpeechSynthesisUtterance(text),
      onText: (text) => callbacks.current.onText(text),
      onSubmit: (text) => { controller.current?.submitted(); callbacks.current.onSubmit(text); },
      onError: (text) => { controller.current = null; callbacks.current.onError(text); },
      onState: (next) => { setState(next); if (!next.active) controller.current = null; },
    });
    controller.current.start();
  }, []);
  useEffect(() => { if (!isOpen) stop(); return stop; }, [isOpen, scopeKey, stop]);
  return { ...state, stop, speak, toggle };
}
